import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useHashScroll from '../hooks/useHashScroll';

const Navigation = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { scrollToElement } = useHashScroll();

  // Handle hamburger menu toggle
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking on nav links
  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  // Handle dropdown toggle
  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  // Handle same-page navigation with smooth scrolling
  const handleSamePageNavigation = (e, targetPath, hash) => {
    const currentPath = location.pathname;
    const targetPagePath = targetPath.split('#')[0];
    
    // If we're on the same page and there's a hash, scroll instead of navigate
    if (currentPath === targetPagePath && hash) {
      e.preventDefault();
      scrollToElement(hash);
      closeMenu();
    }
  };

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <div className="nav-logo">
          <Link to="/" onClick={closeMenu}>
            <img src="/DentoxcellenceLogo.png" alt="DentoXcellence Logo" className="logo-image" />
          </Link>
        </div>
        <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          {/* Accueil Dropdown */}
          <div className="nav-dropdown">
            <button 
              className={`nav-link dropdown-toggle ${location.pathname === '/' ? 'active' : ''}`}
              onClick={() => toggleDropdown('accueil')}
            >
              Accueil <i className={`fas fa-chevron-down ${activeDropdown === 'accueil' ? 'rotated' : ''}`}></i>
            </button>
            <div className={`dropdown-menu ${activeDropdown === 'accueil' ? 'show' : ''}`}>
              <Link to="/#hero" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#hero", "hero"); closeMenu();}}>
                <i className="fas fa-home"></i> Présentation
              </Link>
              <Link to="/#featured-programs" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#featured-programs", "featured-programs"); closeMenu();}}>
                <i className="fas fa-graduation-cap"></i> Formations Phares
              </Link>
              <Link to="/#features" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#features", "features"); closeMenu();}}>
                <i className="fas fa-star"></i> Pourquoi Nous Choisir
              </Link>
              <Link to="/#expert-testimonials" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#expert-testimonials", "expert-testimonials"); closeMenu();}}>
                <i className="fas fa-user-md"></i> Témoignages de nos Experts
              </Link>
              <Link to="/#participant-testimonials" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#participant-testimonials", "participant-testimonials"); closeMenu();}}>
                <i className="fas fa-comments"></i> Témoignages de nos Participants
              </Link>
              <Link to="/#upcoming-sessions" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#upcoming-sessions", "upcoming-sessions"); closeMenu();}}>
                <i className="fas fa-calendar"></i> Prochaines Sessions
              </Link>
            </div>
          </div>

          {/* À propos Dropdown */}
          <div className="nav-dropdown">
            <button 
              className={`nav-link dropdown-toggle ${location.pathname === '/about' ? 'active' : ''}`}
              onClick={() => toggleDropdown('about')}
            >
              À propos de nous <i className={`fas fa-chevron-down ${activeDropdown === 'about' ? 'rotated' : ''}`}></i>
            </button>
            <div className={`dropdown-menu ${activeDropdown === 'about' ? 'show' : ''}`}>
              <Link to="/about#core-values" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#core-values", "core-values"); closeMenu();}}>
                <i className="fas fa-heart"></i> Nos Valeurs Fondamentales
              </Link>
              <Link to="/about#three-blocks" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#three-blocks", "three-blocks"); closeMenu();}}>
                <i className="fas fa-cubes"></i> Histoire, Vision & Mission
              </Link>
              <Link to="/about#chronologie" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#chronologie", "chronologie"); closeMenu();}}>
                <i className="fas fa-timeline"></i> Notre Historique - Chronologie
              </Link>
            </div>
          </div>
          
          {/* Services Dropdown */}
          <div className="nav-dropdown">
            <button 
              className={`nav-link dropdown-toggle ${location.pathname === '/services' ? 'active' : ''}`}
              onClick={() => toggleDropdown('services')}
            >
              Services <i className={`fas fa-chevron-down ${activeDropdown === 'services' ? 'rotated' : ''}`}></i>
            </button>
            <div className={`dropdown-menu ${activeDropdown === 'services' ? 'show' : ''}`}>
              <Link to="/services#overview" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/services#overview", "overview"); closeMenu();}}>
                <i className="fas fa-info-circle"></i> Vue d'Ensemble
              </Link>
              <Link to="/services#seminaires" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/services#seminaires", "seminaires"); closeMenu();}}>
                <i className="fas fa-chalkboard-teacher"></i> Séminaires
              </Link>
              <Link to="/services#formation" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/services#formation", "formation"); closeMenu();}}>
                <i className="fas fa-graduation-cap"></i> Formation
              </Link>
              <Link to="/services#coaching" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/services#coaching", "coaching"); closeMenu();}}>
                <i className="fas fa-user-tie"></i> Coaching
              </Link>
              <Link to="/services#consulting" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/services#consulting", "consulting"); closeMenu();}}>
                <i className="fas fa-lightbulb"></i> Consulting
              </Link>
            </div>
          </div>

          {/* Formations Dropdown */}
          <div className="nav-dropdown">
            <button 
              className={`nav-link dropdown-toggle ${location.pathname === '/formations' ? 'active' : ''}`}
              onClick={() => toggleDropdown('formations')}
            >
              Formations <i className={`fas fa-chevron-down ${activeDropdown === 'formations' ? 'rotated' : ''}`}></i>
            </button>
            <div className={`dropdown-menu ${activeDropdown === 'formations' ? 'show' : ''}`}>
              <Link to="/formations#implantologie" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#implantologie", "implantologie"); closeMenu();}}>
                <i className="fas fa-cog"></i> Implantologie
              </Link>
              <Link to="/formations#orthodontie" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#orthodontie", "orthodontie"); closeMenu();}}>
                <i className="fas fa-align-center"></i> Orthodontie
              </Link>
              <Link to="/formations#chirurgie-orale" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#chirurgie-orale", "chirurgie-orale"); closeMenu();}}>
                <i className="fas fa-teeth"></i> Chirurgie Orale
              </Link>
              <Link to="/formations#cone-beam" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#cone-beam", "cone-beam"); closeMenu();}}>
                <i className="fas fa-x-ray"></i> Cone Beam-CT
              </Link>
              <Link to="/formations#prothese-fixee" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#prothese-fixee", "prothese-fixee"); closeMenu();}}>
                <i className="fas fa-crown"></i> Prothèse Fixée
              </Link>
              <Link to="/formations#endodontie" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#endodontie", "endodontie"); closeMenu();}}>
                <i className="fas fa-tooth"></i> Endodontie
              </Link>
              <Link to="/formations#dentisterie-restauratrice" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#dentisterie-restauratrice", "dentisterie-restauratrice"); closeMenu();}}>
                <i className="fas fa-paint-brush"></i> Dentisterie Restauratrice
              </Link>
              <Link to="/formations#dentisterie-esthetique" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#dentisterie-esthetique", "dentisterie-esthetique"); closeMenu();}}>
                <i className="fas fa-smile"></i> Dentisterie Esthétique
              </Link>
              <Link to="/formations#dentisterie-numerique" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#dentisterie-numerique", "dentisterie-numerique"); closeMenu();}}>
                <i className="fas fa-laptop"></i> Dentisterie Numérique
              </Link>
              <Link to="/formations#calendar" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#calendar", "calendar"); closeMenu();}}>
                <i className="fas fa-calendar-alt"></i> Calendrier Global
              </Link>
              <Link to="/formations#registration-form" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/formations#registration-form", "registration-form"); closeMenu();}}>
                <i className="fas fa-user-plus"></i> Formulaire d'Inscription
              </Link>
            </div>
          </div>

          {/* Experts Dropdown */}
          <div className="nav-dropdown">
            <button 
              className={`nav-link dropdown-toggle ${location.pathname === '/experts' ? 'active' : ''}`}
              onClick={() => toggleDropdown('experts')}
            >
              Experts <i className={`fas fa-chevron-down ${activeDropdown === 'experts' ? 'rotated' : ''}`}></i>
            </button>
            <div className={`dropdown-menu ${activeDropdown === 'experts' ? 'show' : ''}`}>
              <Link to="/experts#expert-team" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/experts#expert-team", "expert-team"); closeMenu();}}>
                <i className="fas fa-users"></i> Nos Formateurs Experts
              </Link>
              <Link to="/experts#dr-ben-maamar" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/experts#dr-ben-maamar", "dr-ben-maamar"); closeMenu();}}>
                <i className="fas fa-user-md"></i> Dr. Ben Maamar
              </Link>
              <Link to="/experts#dr-reverien" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/experts#dr-reverien", "dr-reverien"); closeMenu();}}>
                <i className="fas fa-user-md"></i> Dr. Réverien
              </Link>
              <Link to="/experts#dr-mtallah" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/experts#dr-mtallah", "dr-mtallah"); closeMenu();}}>
                <i className="fas fa-user-md"></i> Dr. Mtallah
              </Link>
            </div>
          </div>

          {/* Contact Dropdown */}
          <div className="nav-dropdown">
            <button 
              className={`nav-link dropdown-toggle ${location.pathname === '/contact' ? 'active' : ''}`}
              onClick={() => toggleDropdown('contact')}
            >
              Contact <i className={`fas fa-chevron-down ${activeDropdown === 'contact' ? 'rotated' : ''}`}></i>
            </button>
            <div className={`dropdown-menu ${activeDropdown === 'contact' ? 'show' : ''}`}>
              <Link to="/contact#contact-info" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#contact-info", "contact-info"); closeMenu();}}>
                <i className="fas fa-info-circle"></i> Informations
              </Link>
              <Link to="/contact#contact-form" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#contact-form", "contact-form"); closeMenu();}}>
                <i className="fas fa-envelope"></i> Formulaire
              </Link>
              <Link to="/contact#quick-actions" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#quick-actions", "quick-actions"); closeMenu();}}>
                <i className="fas fa-bolt"></i> Actions Rapides
              </Link>
              <Link to="/contact#locations" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#locations", "locations"); closeMenu();}}>
                <i className="fas fa-map-marker-alt"></i> Lieux de Formation
              </Link>
              <Link to="/contact#registration" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#registration", "registration"); closeMenu();}}>
                <i className="fas fa-user-plus"></i> Inscription
              </Link>
              <Link to="/contact#faq" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#faq", "faq"); closeMenu();}}>
                <i className="fas fa-question-circle"></i> FAQ
              </Link>
              <Link to="/contact#map" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#map", "map"); closeMenu();}}>
                <i className="fas fa-map"></i> Localisation
              </Link>
            </div>
          </div>
          <Link to="/contact#contact-form" className="nav-phone" onClick={(e) => {handleSamePageNavigation(e, "/contact#contact-form", "contact-form"); closeMenu();}}>
            <i className="fas fa-user-plus"></i> Inscription
          </Link>
        </div>
        <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
